import { useContext, useState } from 'react';

import { faCircleXmark, faPersonFalling } from '@fortawesome/pro-light-svg-icons';
import { Box, Button, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import { AnalyticsContext, ObjectTypes } from 'src/AnalyticsContext';
import LargeIconSelectButton from 'src/components/fragments/LargeIconSelectButton';
import { AlertOutcomeTypes, type AlertOutcomeType } from 'src/constants/AlertOutcomeTypes';

const FALL_ID = 'fall';
const NO_FALL_ID = 'no-fall';

const outcomeById: Record<string, AlertOutcomeType> = {
    [FALL_ID]: AlertOutcomeTypes.ResidentInteraction,
    [NO_FALL_ID]: AlertOutcomeTypes.FalseAlarm,
};

export type DetectReviewOutcomeDrawerProps = DrawerComponentProps;

const DrawerName = 'Detect Review Outcome Drawer';

function DetectReviewOutcomeDrawer(props: DetectReviewOutcomeDrawerProps) {
    const { onNext } = props;
    const { trackEvent } = useContext(AnalyticsContext);
    const { palette } = useTheme();

    const [selectedId, setSelectedId] = useState<string | null>(null);

    const handleSelect = (id: string, isSelected: boolean) => {
        trackEvent('Detect Review Outcome Selected', {
            objectType: ObjectTypes.Button,
            context: DrawerName,
            outcomeId: id,
            isSelected,
        });

        if (isSelected) {
            setSelectedId(id);
        } else {
            setSelectedId((prev) => (prev === id ? null : prev));
        }
    };

    const handleCompleteReview = () => {
        if (!selectedId || !outcomeById[selectedId]) return;
        const alertOutcomeType = outcomeById[selectedId];
        trackEvent('Detect Review Outcome Complete', {
            objectType: ObjectTypes.Button,
            context: DrawerName,
            alertOutcomeType,
        });
        onNext({ alertOutcomeType });
    };

    const canComplete = selectedId !== null;

    return (
        <>
            <DrawerTitle title="Did the resident fall?" />
            <DrawerBody>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: 1.5,
                    }}
                    role="group"
                    aria-label="Fall or no fall"
                >
                    <LargeIconSelectButton
                        id={NO_FALL_ID}
                        label="No Fall"
                        icon={faCircleXmark}
                        iconSize="3x"
                        iconColor={palette.primary.main}
                        isSelected={selectedId === NO_FALL_ID}
                        onSelect={handleSelect}
                    />
                    <LargeIconSelectButton
                        id={FALL_ID}
                        label="Fall"
                        icon={faPersonFalling}
                        iconSize="3x"
                        iconColor={palette.error.main}
                        isSelected={selectedId === FALL_ID}
                        onSelect={handleSelect}
                    />
                </Box>
            </DrawerBody>
            <DrawerFooter>
                <Stack sx={{ gap: 2 }}>
                    <Button
                        fullWidth
                        color="primary"
                        variant="contained"
                        disabled={!canComplete}
                        onClick={handleCompleteReview}
                    >
                        Complete Review
                    </Button>
                </Stack>
            </DrawerFooter>
        </>
    );
}

export default DetectReviewOutcomeDrawer;
